import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { trackPhoneCall, trackTextMessage, trackBookingStart } from "@/components/GoogleAnalytics";
import UrgencyBanner from "@/components/UrgencyBanner";
import Header from "@/components/Header";
import HeroSection from "@/components/HeroSection";
import ServicesSection from "@/components/ServicesSection";
import AddOnsSection from "@/components/AddOnsSection";
import PricingPackages from "@/components/PricingPackages";
import WhyChooseSection from "@/components/WhyChooseSection";
import BeforeAfterGallery from "@/components/BeforeAfterGallery";
import ProcessSection from "@/components/ProcessSection";
import TestimonialsSection from "@/components/TestimonialsSection";
import FAQSection from "@/components/FAQSection";
import ContactCTASection from "@/components/ContactCTASection";
import Footer from "@/components/Footer";
import { useEffect, useState } from "react";
import EstimateDialog from "@/components/EstimateDialog";
import StickyCTA from "@/components/StickyCTA";
import StickyBookingCTA from "@/components/StickyBookingCTA";
import HowItWorksSection from "@/components/HowItWorksSection";
import GoogleReviewsSection from "@/components/GoogleReviewsSection";
import LeadMagnetPopup from "@/components/LeadMagnetPopup";


const Index = () => {
  const navigate = useNavigate();
  const [estimateOpen, setEstimateOpen] = useState(false);

  const handleBookingClick = () => {
    navigate("/book");
  };

  const handleEstimateClick = () => setEstimateOpen(true);
  useEffect(() => {
    // SEO Meta Tags - Updated for Southwest Michigan focus
    document.title = "Professional Organizer & House Cleaning Southwest Michigan | Organize by Design";
    const desc = "Top-rated professional organizing & house cleaning in Southwest Michigan. Home organization, decluttering, deep cleaning, ADHD systems, real estate staging. Serving St. Joseph, Niles, Buchanan, Benton Harbor & Berrien County. Free consultation.";
    
    // Meta description
    let meta = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", desc);

    // Keywords - Updated for target services
    let keywords = document.querySelector('meta[name="keywords"]') as HTMLMetaElement | null;
    if (!keywords) {
      keywords = document.createElement("meta");
      keywords.setAttribute("name", "keywords");
      document.head.appendChild(keywords);
    }
    keywords.setAttribute("content", "professional organizer near me, house cleaning near me, home organization Southwest Michigan, cleaning service Berrien County, professional organizer St Joseph MI, house cleaning Niles Michigan, decluttering services Buchanan, maid service Benton Harbor, ADHD organizing Michigan, real estate staging Southwest Michigan, deep cleaning near me, home organizer near me, organizing services Michigan, cleaning lady near me, best house cleaner Michigan");

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", window.location.origin);

    // Open Graph tags - Updated
    const ogTags = [
      { property: "og:title", content: "Professional Organizer & House Cleaning Southwest Michigan" },
      { property: "og:description", content: desc },
      { property: "og:type", content: "website" },
      { property: "og:url", content: window.location.origin },
      { property: "og:site_name", content: "Organize by Design Michigan" },
      { property: "og:image", content: `${window.location.origin}/lovable-uploads/ad558071-0db2-4301-9fb5-c1235ec6c9b2.png` },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:locale", content: "en_US" }
    ];

    ogTags.forEach(tag => {
      let ogTag = document.querySelector(`meta[property="${tag.property}"]`) as HTMLMetaElement | null;
      if (!ogTag) {
        ogTag = document.createElement("meta");
        ogTag.setAttribute("property", tag.property);
        document.head.appendChild(ogTag);
      }
      ogTag.setAttribute("content", tag.content);
    });

    // Twitter Card tags - Updated
    const twitterTags = [
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Professional Organizer & House Cleaning Southwest Michigan" },
      { name: "twitter:description", content: desc },
      { name: "twitter:image", content: `${window.location.origin}/lovable-uploads/ad558071-0db2-4301-9fb5-c1235ec6c9b2.png` }
    ];

    twitterTags.forEach(tag => {
      let twitterTag = document.querySelector(`meta[name="${tag.name}"]`) as HTMLMetaElement | null;
      if (!twitterTag) {
        twitterTag = document.createElement("meta");
        twitterTag.setAttribute("name", tag.name);
        document.head.appendChild(twitterTag);
      }
      twitterTag.setAttribute("content", tag.content);
    });

    // Enhanced JSON-LD Structured Data
    const ld = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "@id": "https://organizebydesignmichigan.com",
      name: "Organize by Design Michigan",
      alternateName: "Organize by Design",
      description: "Professional luxury home organization services specializing in ADHD-friendly systems, real estate preparation, and comprehensive home transformations across Michigan.",
      url: window.location.origin,
      telephone: "616 727-0343",
      email: "hello@organizebydesignmichigan.com",
      priceRange: "$$$",
      paymentAccepted: "Cash, Check, Credit Card",
      currenciesAccepted: "USD",
      foundingDate: "2015",
      founder: {
        "@type": "Person",
        name: "Organize by Design Team"
      },
      address: {
        "@type": "PostalAddress",
        addressRegion: "MI",
        addressCountry: "US",
        addressLocality: "Michigan"
      },
      geo: {
        "@type": "GeoCoordinates",
        latitude: "44.3148",
        longitude: "-85.6024"
      },
      areaServed: [
        {
          "@type": "State",
          name: "Michigan",
          containsPlace: [
            { "@type": "City", name: "Buchanan", addressRegion: "MI" },
            { "@type": "City", name: "Niles", addressRegion: "MI" },
            { "@type": "City", name: "St. Joseph", addressRegion: "MI" },
            { "@type": "City", name: "Benton Harbor", addressRegion: "MI" },
            { "@type": "City", name: "Berrien Springs", addressRegion: "MI" },
            { "@type": "City", name: "Stevensville", addressRegion: "MI" },
            { "@type": "City", name: "Bridgman", addressRegion: "MI" },
            { "@type": "City", name: "New Buffalo", addressRegion: "MI" },
            { "@type": "City", name: "Dowagiac", addressRegion: "MI" },
            { "@type": "City", name: "Edwardsburg", addressRegion: "MI" }
          ]
        }
      ],
      serviceType: [
        "Home Organization",
        "Professional Organizing", 
        "ADHD Organization Systems",
        "Real Estate Staging Preparation",
        "Luxury Home Organization",
        "Emergency Decluttering",
        "Commercial Organization"
      ],
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: "Home Organization Services",
        itemListElement: [
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "72-Hour Home Reset",
              description: "Complete home transformation in 72 hours"
            }
          },
          {
            "@type": "Offer", 
            itemOffered: {
              "@type": "Service",
              name: "ADHD-Friendly Systems",
              description: "Specialized organizing for neurodivergent minds"
            }
          },
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service", 
              name: "Real Estate Prep",
              description: "Home staging and preparation for market"
            }
          },
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "Same Day Emergency",
              description: "Emergency decluttering and organizing"
            }
          }
        ]
      },
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: "5",
        reviewCount: "47",
        bestRating: "5",
        worstRating: "5"
      },
      review: [
        {
          "@type": "Review",
          author: { "@type": "Person", name: "Sarah M." },
          reviewRating: { "@type": "Rating", ratingValue: "5" },
          reviewBody: "Transformed our entire home in 3 days. The ADHD-friendly systems actually work!"
        }
      ],
      sameAs: [
        "https://www.facebook.com/organizebydesignmichigan",
        "https://www.instagram.com/organizebydesignmi"
      ],
      image: `${window.location.origin}/lovable-uploads/ad558071-0db2-4301-9fb5-c1235ec6c9b2.png`,
      logo: `${window.location.origin}/src/assets/logo.png`,
      openingHoursSpecification: {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        opens: "08:00",
        closes: "18:00"
      }
    } as const;
    
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify(ld);
    document.head.appendChild(script);
    
    return () => { 
      if (document.head.contains(script)) document.head.removeChild(script); 
    };
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <UrgencyBanner />
      <Header onBookingClick={handleBookingClick} />
      <main className="relative">
        {/* Hero Section */}
        <HeroSection onBookingClick={handleBookingClick} />

        {/* Services Overview - Primary CTA */}
        <section className="py-24 bg-gradient-to-br from-background via-muted/30 to-background">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16 fade-in-up">
              <h2 className="text-4xl md:text-5xl font-serif mb-6">Our Specialized Services</h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Home organization, professional cleaning, and life transition support — 
                designed for clarity, calm, and lasting results.
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-12">
              <div 
                onClick={() => navigate('/organizing')}
                className="group relative bg-card border border-border rounded-2xl p-8 hover-lift cursor-pointer overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-accent/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <div className="relative z-10">
                  <h3 className="text-2xl font-serif mb-4">Home Organization</h3>
                  <p className="text-muted-foreground mb-6">
                    Home Office • Family & Kitchen • Closet & Seasonal • Moving Support
                  </p>
                  <Button variant="outline" className="group-hover:bg-primary group-hover:text-primary-foreground">
                    Explore Organizing
                  </Button>
                </div>
              </div>
              
              <div 
                onClick={() => navigate('/cleaning')}
                className="group relative bg-card border border-border rounded-2xl p-8 hover-lift cursor-pointer overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-accent/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <div className="relative z-10">
                  <h3 className="text-2xl font-serif mb-4">Professional Cleaning</h3>
                  <p className="text-muted-foreground mb-6">
                    Residential • Commercial • Move In/Out • Same-Day Service
                  </p>
                  <Button variant="outline" className="group-hover:bg-primary group-hover:text-primary-foreground">
                    Explore Cleaning
                  </Button>
                </div>
              </div>

              <div 
                onClick={() => {
                  const el = document.getElementById('services');
                  el?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="group relative bg-card border border-border rounded-2xl p-8 hover-lift cursor-pointer overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-accent/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <div className="relative z-10">
                  <h3 className="text-2xl font-serif mb-4">Estate & Life Transitions</h3>
                  <p className="text-muted-foreground mb-6">
                    Senior Downsizing • College Setup • Real Estate Prep & Staging
                  </p>
                  <Button variant="outline" className="group-hover:bg-primary group-hover:text-primary-foreground">
                    Explore Transitions
                  </Button>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button 
                onClick={() => {
                  trackBookingStart();
                  navigate('/book');
                }} 
                size="lg" 
                className="luxury-gradient text-primary-foreground font-semibold px-8 py-6 text-lg"
              >
                Book Free Consultation
              </Button>
              <Button 
                variant="outline" 
                size="lg"
                onClick={() => {
                  trackPhoneCall();
                  window.open('tel:+16167270343');
                }}
                className="px-8 py-6 text-lg"
              >
                Call 616.727.0343
              </Button>
            </div>
          </div>
        </section>

        {/* Detailed Services */}
        <ServicesSection onBookingClick={handleBookingClick} onEstimateClick={handleEstimateClick} />
        
        {/* Pricing Packages */}
        <PricingPackages />
        
        {/* How It Works - Process Overview */}
        <HowItWorksSection onBookingClick={handleBookingClick} />
        
        {/* Why Choose Us - Trust Building */}
        <WhyChooseSection />
        
        {/* Before & After - Social Proof */}
        <BeforeAfterGallery />
        
        {/* Testimonials - Social Proof */}
        <TestimonialsSection />
        
        {/* Google Reviews */}
        <GoogleReviewsSection />
        
        {/* Add-Ons & Extras */}
        <AddOnsSection onBookingClick={handleBookingClick} onEstimateClick={handleEstimateClick} />
        
        {/* Process Details */}
        <ProcessSection />
        
        {/* FAQ Section */}
        <FAQSection />
        
        {/* Final CTA */}
        <ContactCTASection onBookingClick={handleBookingClick} />
      </main>
      <Footer onBookingClick={handleBookingClick} />
      <EstimateDialog open={estimateOpen} onOpenChange={setEstimateOpen} />
      <StickyBookingCTA onBookingClick={handleBookingClick} />
      <LeadMagnetPopup />
    </div>
  );
};

export default Index;
